import * as React from "react";

import {
  RadioGroup,
  RadioGroupItem,
} from "@replohq/lattice/components/radio-group/RadioGroup";
import { Tag } from "@replohq/lattice/components/tag/Tag";
import { Screen } from "@v2/features/account/common";
import { AgentSetupCard } from "@v2/features/onboarding/components/AgentSetupCard";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { Navigate, useLocation } from "react-router-dom";

import { AgentWelcomeScreen } from "./AgentWelcomeScreen";
import { StepActions } from "./components/StepActions";
import {
  NEW_WORKSPACE_OPTION_ID,
  useAgentEnableFlow,
} from "./useAgentEnableFlow";

// (Yuxin, 2026-07-18): Full-screen post-login step — the account exists, this is just "which workspace opts into the beta?".
const AgentEnableScreen: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <div className="relative flex min-h-screen w-full flex-col items-center bg-surface px-4 py-16">
      <img
        src="/images/onboarding/replo-wordmark-black.svg"
        alt="Replo"
        className="absolute left-5 top-5 h-7 w-auto md:left-8 md:top-7"
      />
      <div className="flex w-full max-w-xl flex-1 flex-col justify-center">
        {children}
      </div>
    </div>
  );
};

const LoadingScreen: React.FC = () => {
  return (
    <AgentEnableScreen>
      <div className="flex min-h-64 items-center justify-center">
        <p className="text-sm text-muted">Getting things ready…</p>
      </div>
    </AgentEnableScreen>
  );
};

const AgentEnableFlow: React.FC = () => {
  const {
    step,
    source,
    submitState,
    eligibleWorkspaces,
    selectedWorkspaceId,
    projectName,
    setSelectedWorkspaceId,
    setProjectName,
    continueFromWelcome,
    continueFromWorkspace,
    submitProject,
    goBackToWorkspace,
  } = useAgentEnableFlow();

  if (step === "loading") {
    return <LoadingScreen />;
  }

  if (step === "welcome") {
    return <AgentWelcomeScreen source={source} onOptIn={continueFromWelcome} />;
  }

  // Reuses the onboarding identity card (same visuals + fly-to-launchpad animation) with the safe no-rename submit.
  if (step === "project") {
    return (
      <AgentSetupCard
        heading={{
          title: "Name your project",
          subtitle: "You can rename it anytime.",
        }}
        fields={[
          {
            label: "Project",
            value: projectName,
            onChange: setProjectName,
            placeholder: "Acme",
            autoFocus: true,
          },
        ]}
        isFormValid={projectName.trim().length > 0}
        onSubmit={submitProject}
        submitLabel="Create project"
        onBack={eligibleWorkspaces.length > 0 ? goBackToWorkspace : undefined}
      />
    );
  }

  return (
    <AgentEnableScreen>
      <div className="flex w-full flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="font-editorial text-3xl font-bold leading-tight text-default md:text-4xl">
            Which workspace would you like to opt into the beta?
          </h1>
        </div>

        <div className="flex w-full flex-col gap-5">
          <div className="relative">
            <div className="max-h-96 overflow-y-auto pb-6 pr-1">
              <RadioGroup
                aria-label="Workspaces"
                value={selectedWorkspaceId}
                onValueChange={(value) => setSelectedWorkspaceId(String(value))}
              >
                {eligibleWorkspaces.map((workspace) => (
                  <label
                    key={workspace.id}
                    htmlFor={workspace.id}
                    className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-border bg-surface p-3 transition-colors hover:bg-selectable-hover has-data-checked:border-component-border has-data-checked:bg-sheet"
                  >
                    <RadioGroupItem value={workspace.id} id={workspace.id} />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-default">
                      {workspace.name}
                    </span>
                    {workspace.isAgentEnabled && (
                      <Tag variant="success" size="sm">
                        Beta
                      </Tag>
                    )}
                  </label>
                ))}
                <label
                  htmlFor={NEW_WORKSPACE_OPTION_ID}
                  className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-border bg-surface p-3 transition-colors hover:bg-selectable-hover has-data-checked:border-component-border has-data-checked:bg-sheet"
                >
                  <RadioGroupItem
                    value={NEW_WORKSPACE_OPTION_ID}
                    id={NEW_WORKSPACE_OPTION_ID}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-default">
                    Create a new workspace
                  </span>
                </label>
              </RadioGroup>
            </div>
            {/* (Yuxin, 2026-07-18): Fade the overflowing list so it doesn't look hard-cut. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-surface to-transparent"
            />
          </div>
          <StepActions
            submitLabel="Continue"
            isSubmitting={submitState.status === "submitting"}
            onSubmit={() => void continueFromWorkspace()}
          />
        </div>
      </div>
    </AgentEnableScreen>
  );
};

// /beta/enable: logged-out → standard Agent signup funnel (returns here via `next`); authed → workspace picker. New accounts fall through to App's onboarding gate.
const AgentBetaEnablePage: React.FC = () => {
  const { isAuthenticated, isLoading } = useCurrentUser();
  // Carry source/workspaceId/name through the signup round-trip so the
  // welcome step keeps its attribution and prefills for logged-out arrivals.
  const { search } = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={`${Screen.SIGNUP}?type=agent&next=${encodeURIComponent(`${Screen.BETA_ENABLE}${search}`)}`}
        replace
      />
    );
  }

  return <AgentEnableFlow />;
};

export default AgentBetaEnablePage;
